/**
 * Variant Radio Cards - Minimal JS
 * Only handles showing/hiding variant notes based on Shopify's variant:update event
 * All selection logic handled by Shopify's native variant-picker
 */

// Sync product form when virtual XL bundle is selected (qty 2 or 3)
function syncVirtualBundleToForm(variantPicker, virtualInput) {
  const sectionId = variantPicker.dataset.sectionId;
  if (!sectionId || !virtualInput?.dataset.bundleQuantity || !virtualInput.dataset.variantId) return;

  const productForm = document.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
  if (!productForm) return;

  const form = productForm.querySelector('form');
  if (!form) return;

  const bundleQty = virtualInput.dataset.bundleQuantity;
  const variantId = virtualInput.dataset.variantId;

  const idInput = form.querySelector('input[name="id"]');
  if (idInput) idInput.value = variantId;

  const qtyInput = form.querySelector('input[name="quantity"]');
  if (qtyInput) {
    qtyInput.removeAttribute('max');
    qtyInput.min = '1';
    qtyInput.step = '1';
    qtyInput.value = bundleQty;
  }

  productForm.dataset.virtualBundleActive = 'true';
  productForm.dataset.virtualBundleQuantity = bundleQty;
}

function clearVirtualBundleFromForm(variantPicker) {
  const sectionId = variantPicker.dataset.sectionId;
  if (!sectionId) return;

  const productForm = document.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
  if (!productForm) return;

  productForm.dataset.virtualBundleActive = 'false';
  delete productForm.dataset.virtualBundleQuantity;

  const form = productForm.querySelector('form');
  // FW Oct 2026: point the basket form at the chosen pack straight away, so Add to cart
  // can't add the previous pack while the theme is still fetching the updated price
  const chosen = variantPicker.querySelector('input[type="radio"]:checked');
  const idField = form?.querySelector('input[name="id"]');
  if (idField instanceof HTMLInputElement && chosen?.dataset.variantId && chosen.dataset.virtualBundle !== 'true') {
    idField.value = chosen.dataset.variantId;
  }
  const qtyInput = form?.querySelector('input[name="quantity"]');
  if (qtyInput instanceof HTMLInputElement) {
    qtyInput.value = qtyInput.min || '1';
  }

  const quantitySelector = productForm.querySelector('quantity-selector-component');
  if (quantitySelector && 'setValue' in quantitySelector && typeof quantitySelector.setValue === 'function') {
    quantitySelector.setValue(qtyInput?.min || '1');
    if (typeof quantitySelector.updateButtonStates === 'function') {
      quantitySelector.updateButtonStates();
    }
  }
}

// Show/hide variant notes when variant changes
function updateVariantNotes(variantId) {
  const notesContainer = document.querySelector('.variant-notes-display[data-variant-notes-container]');
  if (!notesContainer) return;
  
  const allNoteItems = notesContainer.querySelectorAll('.variant-notes-display__item');
  let hasVisibleContent = false;
  
  allNoteItems.forEach(item => {
    if (item.dataset.variantId === variantId) {
      const hasContent = item.querySelector('.variant-notes-display__content');
      if (hasContent && hasContent.textContent.trim() !== '') {
        item.style.display = 'block';
        hasVisibleContent = true;
      } else {
        item.style.display = 'none';
      }
    } else {
      item.style.display = 'none';
    }
  });
  
  notesContainer.style.display = hasVisibleContent ? 'block' : 'none';
  
  // Also update notes container after buy buttons if it exists
  const buyButtonsBlock = document.querySelector('.buy-buttons-block') || 
                         document.querySelector('product-form-component') ||
                         document.querySelector('[data-type="add-to-cart-form"]');
  
  if (buyButtonsBlock) {
    const notesAfterButton = buyButtonsBlock.nextElementSibling;
    if (notesAfterButton && notesAfterButton.classList.contains('variant-notes-display')) {
      const allNoteItemsAfter = notesAfterButton.querySelectorAll('.variant-notes-display__item');
      let hasVisibleContentAfter = false;
      
      allNoteItemsAfter.forEach(item => {
        if (item.dataset.variantId === variantId) {
          const hasContent = item.querySelector('.variant-notes-display__content');
          if (hasContent && hasContent.textContent.trim() !== '') {
            item.style.display = 'block';
            hasVisibleContentAfter = true;
          } else {
            item.style.display = 'none';
          }
        } else {
          item.style.display = 'none';
        }
      });
      
      notesAfterButton.style.display = hasVisibleContentAfter ? 'block' : 'none';
    }
  }
}

// Extract text from rich text JSON for shipping text
function extractTextFromRichTextJSON(json) {
  if (typeof json === 'string') {
    try {
      json = JSON.parse(json);
    } catch (e) {
      return json;
    }
  }
  if (!json || typeof json !== 'object') return '';
  
  let text = '';
  if (json.children && Array.isArray(json.children)) {
    json.children.forEach(child => {
      if (child.type === 'text' && child.value) {
        text += child.value;
      } else if (child.type === 'paragraph' && child.children) {
        child.children.forEach(paraChild => {
          if (paraChild.type === 'text' && paraChild.value) {
            text += paraChild.value;
          }
        });
        text += ' ';
      } else if (child.children) {
        text += extractTextFromRichTextJSON(child);
      } else if (child.value) {
        text += child.value;
      }
    });
  } else if (json.value) {
    text = json.value;
  }
  
  return text.trim();
}

// Update shipping texts from rich text JSON (one-time on load)
function updateShippingTexts() {
  const shippingElements = document.querySelectorAll('.variant-radio-card__shipping[data-notes-json]');
  shippingElements.forEach(element => {
    const textSpan = element.querySelector('.variant-radio-card__shipping-text');
    if (!textSpan) return;
    
    // First check if there's initial text from Liquid
    const initialText = element.dataset.initialText;
    if (initialText && initialText.trim() !== '') {
      if (!textSpan.textContent || textSpan.textContent.trim() === '') {
        textSpan.textContent = initialText;
      }
      element.style.display = 'flex';
      return;
    }
    
    // Check if textSpan already has content from Liquid
    if (textSpan.textContent && textSpan.textContent.trim() !== '') {
      element.style.display = 'flex';
      return;
    }
    
    // Otherwise, try to extract from JSON
    const jsonData = element.dataset.notesJson;
    if (jsonData) {
      try {
        const extractedText = extractTextFromRichTextJSON(jsonData);
        if (extractedText && extractedText.trim() !== '') {
          textSpan.textContent = extractedText;
          element.style.display = 'flex';
        } else {
          element.style.display = 'none';
        }
      } catch (e) {
        console.warn('Could not parse shipping text JSON:', e);
        // Don't hide if there's already text content
        if (!textSpan.textContent || textSpan.textContent.trim() === '') {
          element.style.display = 'none';
        }
      }
    } else {
      // If no JSON and no initial text, only hide if empty
      if (!textSpan.textContent || textSpan.textContent.trim() === '') {
        element.style.display = 'none';
      } else {
        element.style.display = 'flex';
      }
    }
  });
}

// Initialize
function initVariantRadioCards() {
  const variantPicker = document.querySelector('.variant-picker--radio-cards');
  if (!variantPicker) return;
  
  // Listen to Shopify's variant:update event
  const section = variantPicker.closest('.shopify-section, section');
  if (section) {
    section.addEventListener('variant:update', (event) => {
      const variantId = event.detail?.resource?.id;
      const sourceId = event.detail?.sourceId;
      const isVirtualBundle = event.detail?.data?.virtualBundle;

      if (variantId) {
        updateVariantNotes(variantId.toString());
      }

      // Show virtual bundle description from data attribute when selected
      if (isVirtualBundle && sourceId) {
        const bundleInput = variantPicker.querySelector(`input[data-option-value-id="${sourceId}"]`);
        const descriptionText = bundleInput?.dataset.descriptionText;
        if (descriptionText) {
          const notesContainer = document.querySelector('.variant-notes-display[data-variant-notes-container]');
          if (notesContainer) {
            notesContainer.innerHTML = `<div class="variant-notes-display__content">${descriptionText}</div>`;
            notesContainer.style.display = 'block';
          }
        }
      }

      // After variant update, ensure the correct radio stays checked (match by option value id)
      if (sourceId) {
        setTimeout(() => {
          const allRadios = variantPicker.querySelectorAll('input[type="radio"]');
          allRadios.forEach((radio) => {
            const isMatch = radio.dataset.optionValueId === sourceId;
            radio.checked = isMatch;
            radio.dataset.currentChecked = isMatch ? 'true' : 'false';
            if (!isMatch) {
              radio.dataset.previousChecked = 'false';
            }
          });
        }, 50);
      }
    });
  }
  
  // Sync form qty on capture (before variant-picker section fetch / cart sync)
  variantPicker.addEventListener(
    'change',
    (e) => {
      if (!(e.target instanceof HTMLInputElement) || e.target.type !== 'radio') return;
      if (e.target.dataset.virtualBundle === 'true') {
        syncVirtualBundleToForm(variantPicker, e.target);
      } else {
        clearVirtualBundleFromForm(variantPicker);
      }
    },
    true
  );

  // Also listen to change events on radio inputs (notes + description)
  variantPicker.addEventListener('change', (e) => {
    if (e.target && e.target.type === 'radio') {
      if (e.target.dataset.virtualBundle === 'true') {
        if (e.target.dataset.descriptionText) {
          const notesContainer = document.querySelector('.variant-notes-display[data-variant-notes-container]');
          if (notesContainer) {
            notesContainer.innerHTML = `<div class="variant-notes-display__content">${e.target.dataset.descriptionText}</div>`;
            notesContainer.style.display = 'block';
          }
        }
      } else if (e.target.dataset.variantId) {
        updateVariantNotes(e.target.dataset.variantId);
      }
    }
  });
  
  // Initialize with current variant
  const checkedInput = variantPicker.querySelector('input[type="radio"]:checked');
  if (checkedInput && checkedInput.dataset.variantId) {
    updateVariantNotes(checkedInput.dataset.variantId);
  }
  
  // Update shipping texts once on load
  updateShippingTexts();
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initVariantRadioCards);

// Re-initialize on section load (theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      initVariantRadioCards();
    }, 100);
  });
}


/* ------------------------------------------------------------------
 * FW Oct 2026: open the page on a chosen pack, and make the
 * "Save more with XL multi-packs" cards select their pack.
 *   ?pack=3  -> 3 x XL Packs     ?pack=2 -> 2 x XL Packs     ?pack=1 -> XL (10 Servings)
 *   no ?pack and no ?variant -> the default set in the theme editor (data-fw-default-pack)
 * Selection is done with a real click, so the theme's own code updates price,
 * basket form, quantity and sticky bar exactly as if the shopper had tapped it.
 * ------------------------------------------------------------------ */
function fwFindPackRadio(picker, pack) {
  if (!picker) return null;
  const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
  if (pack === '2' || pack === '3') {
    return radios.find((r) => r.dataset.virtualBundle === 'true' && r.dataset.bundleQuantity === pack) || null;
  }
  if (pack === '1') {
    const xlBundle = radios.find((r) => r.dataset.virtualBundle === 'true');
    const xlId = xlBundle ? xlBundle.dataset.variantId : null;
    return radios.find((r) => r.dataset.virtualBundle !== 'true' && xlId && r.dataset.variantId === xlId) || null;
  }
  return null;
}

function fwSelectPack(pack) {
  const picker = document.querySelector('.variant-picker--radio-cards');
  const radio = fwFindPackRadio(picker, String(pack));
  if (!radio || radio.disabled) return false;
  if (!radio.checked) radio.click();
  return true;
}

function fwApplyPackFromUrl() {
  const picker = document.querySelector('.variant-picker--radio-cards');
  if (!picker) return;
  const params = new URLSearchParams(window.location.search);
  let pack = params.get('pack');
  if (!['1', '2', '3'].includes(pack || '')) {
    if (params.has('variant')) return; // a specific variant was linked - leave it alone
    pack = picker.dataset.fwDefaultPack;
    if (!['1', '2', '3'].includes(pack || '')) return; // "0" = original behaviour
  }
  fwSelectPack(pack);
}

function fwWireOfferCards() {
  document.querySelectorAll('[data-fw-pack]').forEach((card) => {
    if (card.dataset.fwWired) return;
    card.dataset.fwWired = 'true';
    const go = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      if (!fwSelectPack(card.dataset.fwPack)) return;
      const target = document.getElementById('product-pack-options');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    card.addEventListener('click', go);
    card.addEventListener('keydown', go);
  });
}

function fwInitPacks() {
  fwWireOfferCards();
  const ready = ['variant-picker', 'product-form-component']
    .filter((name) => document.querySelector(name))
    .map((name) => customElements.whenDefined(name));
  Promise.all(ready).then(() => requestAnimationFrame(() => setTimeout(fwApplyPackFromUrl, 0)));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fwInitPacks);
} else {
  fwInitPacks();
}
